import apiService from './api';
import type {
  SubscriptionPlan,
  LicenseType,
  OrganisationSubscription,
  UserLicense,
  SubscriptionTransaction,
  BillingInfo,
  CustomerTerm,
  ActiveModule,
  AvailableUser,
  LicensedUser,
  ModuleUsageStats,
  PurchaseSubscriptionRequest,
  UpdateSeatsRequest,
  CancelSubscriptionRequest,
  AssignLicenseRequest,
  RevokeLicenseRequest,
  BatchPurchaseRequest,
  BatchPurchaseResponse,
  XeroStatus,
} from '../types/subscription';
import { toNumOr, type Numeric } from '@/utils/numeric';

/**
 * Subscription Service — typed-license API.
 */
class SubscriptionService {
  // ==========================================================================
  // PLANS + LICENSE TYPES
  // ==========================================================================

  async getAvailablePlans() {
    return apiService.get<SubscriptionPlan[]>('/subscriptions/plans');
  }

  async getPlanByModule(moduleKey: string) {
    return apiService.get<SubscriptionPlan>(`/subscriptions/plans/${moduleKey}`);
  }

  async getLicenseTypes(moduleKey: string) {
    return apiService.get<LicenseType[]>(`/subscriptions/plans/${moduleKey}/license-types`);
  }

  // ==========================================================================
  // ORGANISATION SUBSCRIPTIONS
  // ==========================================================================

  async getOrganisationSubscriptions() {
    return apiService.get<OrganisationSubscription[]>('/subscriptions/organisation');
  }

  async getActiveModules() {
    return apiService.get<ActiveModule[]>('/subscriptions/organisation/modules');
  }

  async getBillingInfo() {
    return apiService.get<BillingInfo>('/subscriptions/organisation/billing');
  }

  /** Offers in force for the caller's org (customer view — no internal reasons). */
  async getOrganisationTerms() {
    return apiService.get<CustomerTerm[]>('/subscriptions/organisation/terms');
  }

  async getTransactionHistory(limit: number = 50) {
    return apiService.get<SubscriptionTransaction[]>(
      `/subscriptions/organisation/transactions?limit=${limit}`
    );
  }

  async purchaseSubscription(data: PurchaseSubscriptionRequest) {
    return apiService.post<OrganisationSubscription>('/subscriptions/purchase', {
      ...data,
      payment_method: 'invoice',
    });
  }

  /**
   * Update seat counts per license type for a subscription.
   */
  async updateSeats(subscriptionId: number, data: UpdateSeatsRequest) {
    return apiService.patch<OrganisationSubscription>(
      `/subscriptions/${subscriptionId}/seats`,
      data
    );
  }

  async cancelSubscription(subscriptionId: number, data?: CancelSubscriptionRequest) {
    return apiService.post<OrganisationSubscription>(
      `/subscriptions/${subscriptionId}/cancel`,
      data || {}
    );
  }

  async reactivateSubscription(subscriptionId: number) {
    return apiService.post<OrganisationSubscription>(
      `/subscriptions/${subscriptionId}/reactivate`,
      {}
    );
  }

  // ==========================================================================
  // LICENSE ASSIGNMENTS (typed)
  // ==========================================================================

  async getLicenseAssignments(moduleKey?: string) {
    const url = moduleKey
      ? `/subscriptions/licenses?module=${moduleKey}`
      : '/subscriptions/licenses';
    return apiService.get<UserLicense[]>(url);
  }

  async getMyLicenses() {
    return apiService.get<UserLicense[]>('/subscriptions/licenses/me');
  }

  async getAvailableUsers(moduleKey: string, typeKey: string) {
    return apiService.get<AvailableUser[]>(
      `/subscriptions/licenses/available/${moduleKey}/${typeKey}`
    );
  }

  /**
   * Users in the caller's org holding an active license for `moduleKey`.
   * Use this — not `userService.getUsers()` — anywhere a module UI offers a
   * list of people (alert recipients, assignees). Optionally narrow to
   * specific license tiers.
   */
  async getModuleLicensedUsers(moduleKey: string, typeKeys?: string | string[]) {
    const types = typeKeys == null
      ? []
      : (Array.isArray(typeKeys) ? typeKeys : [typeKeys]);
    const qs = types.length > 0 ? `?types=${encodeURIComponent(types.join(','))}` : '';
    return apiService.get<LicensedUser[]>(
      `/subscriptions/licenses/module/${moduleKey}/users${qs}`
    );
  }

  async assignLicense(data: AssignLicenseRequest) {
    return apiService.post<UserLicense>('/subscriptions/licenses/assign', data);
  }

  async revokeLicense(data: RevokeLicenseRequest) {
    const params = new URLSearchParams();
    params.append('user_id', data.user_id.toString());
    params.append('module_key', data.module_key);
    params.append('type_key', data.type_key);
    return apiService.delete(`/subscriptions/licenses/revoke?${params.toString()}`);
  }

  // ==========================================================================
  // BATCH PURCHASE + INVOICES
  // ==========================================================================

  async batchPurchase(data: BatchPurchaseRequest) {
    return apiService.post<BatchPurchaseResponse>('/subscriptions/batch-purchase', data);
  }

  /**
   * Buy `count` more seats of one tier on an active subscription. Goes through
   * batch-purchase so it is invoiced like any other purchase — the manual
   * seats PATCH is the platform operator's path, not the customer's.
   */
  async addSeats(sub: OrganisationSubscription, typeKey: string, count: number, poReference?: string) {
    const current = sub.seats?.find(s => s.type_key === typeKey)?.total_seats ?? 0;
    return this.batchPurchase({
      items: [{
        type: 'update',
        subscription_id: sub.id,
        seats: [{ type_key: typeKey, total_seats: current + count }],
      }],
      billing_cycle: 'monthly',
      payment_reference: poReference || undefined,
    });
  }

  async getInvoiceHistory(limit: number = 50) {
    return apiService.get<SubscriptionTransaction[]>(
      `/subscriptions/organisation/invoices?limit=${limit}`
    );
  }

  // ==========================================================================
  // XERO
  // ==========================================================================

  async getXeroStatus() {
    return apiService.get<XeroStatus>('/xero/status');
  }

  async getXeroAuthorizationUrl() {
    return apiService.get<{ authorization_url: string }>('/xero/authorize');
  }

  /** Finish the sign-in Xero sent the browser back from (`useOAuthReturn`). */
  async completeXeroSignIn(code: string, state: string) {
    return apiService.post<XeroStatus>('/xero/complete', { code, state }, undefined, { timeout: 30_000 });
  }

  async disconnectXero() {
    return apiService.post('/xero/disconnect', {});
  }

  async retryXeroInvoice(transactionId: number) {
    return apiService.post(`/xero/retry-invoice/${transactionId}`, {});
  }

  async syncXeroStatuses() {
    return apiService.post('/xero/sync-statuses', {});
  }

  // ==========================================================================
  // USAGE
  // ==========================================================================

  async getUsageStats(moduleKey?: string, startDate?: string, endDate?: string) {
    const params = new URLSearchParams();
    if (moduleKey) params.append('module_key', moduleKey);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const query = params.toString();
    const url = query ? `/subscriptions/usage?${query}` : '/subscriptions/usage';
    return apiService.get<ModuleUsageStats[]>(url);
  }

  // ==========================================================================
  // CLIENT-SIDE HELPERS
  // ==========================================================================

  /**
   * Does the org have an active subscription to this module?
   */
  hasModuleAccess(modules: ActiveModule[], moduleKey: string): boolean {
    return modules.some(m => m.module_key === moduleKey && m.status === 'active');
  }

  /**
   * Does the user hold any active license for this module in the given org?
   * Pass orgId to scope to the active org (multi-org safe).
   */
  hasLicense(licenses: UserLicense[], moduleKey: string, orgId?: number | null): boolean {
    return licenses.some(
      l =>
        l.module_key === moduleKey &&
        l.is_active &&
        (orgId == null || l.organisation_id === orgId)
    );
  }

  /**
   * Does the user hold a specific license type (tier) for this module?
   * e.g. hasLicenseType(licenses, 'timesheet', 'hr', orgId)
   */
  hasLicenseType(
    licenses: UserLicense[],
    moduleKey: string,
    typeKey: string,
    orgId?: number | null,
  ): boolean {
    return licenses.some(
      l =>
        l.module_key === moduleKey &&
        l.type_key === typeKey &&
        l.is_active &&
        (orgId == null || l.organisation_id === orgId)
    );
  }

  /**
   * Does the user hold any of the given license types for this module?
   */
  hasAnyLicenseType(
    licenses: UserLicense[],
    moduleKey: string,
    typeKeys: string[],
    orgId?: number | null,
  ): boolean {
    return typeKeys.some(tk => this.hasLicenseType(licenses, moduleKey, tk, orgId));
  }

  formatCurrency(amount: Numeric | undefined, currency: string = 'AUD'): string {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency,
    }).format(toNumOr(amount));
  }

  getModuleDisplayName(moduleKey: string): string {
    const names: Record<string, string> = {
      timesheet: 'Timesheet & Leave',
      irrigation: 'Sensors & Irrigation',
      cultivar: 'Cultivar Registry',
      spray: 'Spray Management',
      vehicles: 'Vehicles & Equipment',
      farm_jobs: 'Farm Jobs',
      nursery: 'Nursery Management',
      accounts: 'Accounts',
    };
    return names[moduleKey] || moduleKey;
  }

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      active: 'success',
      trial: 'warning',
      suspended: 'warning',
      cancelled: 'error',
      pending: 'neutral',
    };
    return colors[status] || 'neutral';
  }

  getInvoiceStatusColor(status: string | null | undefined): string {
    if (!status) return 'bg-neutral-100 text-neutral-700';
    const map: Record<string, string> = {
      PAID: 'bg-green-100 text-green-800',
      AUTHORISED: 'bg-yellow-100 text-yellow-800',
      SUBMITTED: 'bg-yellow-100 text-yellow-800',
      DRAFT: 'bg-neutral-100 text-neutral-700',
      VOIDED: 'bg-red-100 text-red-800',
      FAILED: 'bg-red-100 text-red-800',
      NOT_CONNECTED: 'bg-orange-100 text-orange-800',
      OVERDUE: 'bg-red-100 text-red-800',
    };
    return map[status] || 'bg-neutral-100 text-neutral-700';
  }

  /**
   * The customer's view of an invoice status. The raw values are the
   * accounting system's (and our own failure markers — FAILED, NOT_CONNECTED),
   * which mean something to whoever reconciles billing and nothing to the
   * customer. Anything not yet issued reads as "Processing": from the
   * customer's side it is, and fixing it is the billing team's job.
   */
  customerInvoiceStatus(status: string | null | undefined): { label: string; className: string } {
    switch (status) {
      case 'PAID':
        return { label: 'Paid', className: 'bg-green-100 text-green-800' };
      case 'AUTHORISED':
      case 'SUBMITTED':
        return { label: 'Awaiting payment', className: 'bg-yellow-100 text-yellow-800' };
      case 'OVERDUE':
        return { label: 'Overdue', className: 'bg-red-100 text-red-800' };
      case 'VOIDED':
        return { label: 'Void', className: 'bg-neutral-100 text-neutral-500' };
      default:
        return { label: 'Processing', className: 'bg-neutral-100 text-neutral-700' };
    }
  }
}

export default new SubscriptionService();
